export type ChartPalette = {
  green: string;
  yellow: string;
  orange: string;
  red: string;
};

/**
 * The app's long-standing full-strength traffic-light palette. Nothing renders
 * it directly any more — it is the base every softened palette below is derived
 * from, and the fallback if the softer look is ever pulled back.
 */
export const FULL_PALETTE: ChartPalette = {
  green: '#78F880',
  yellow: '#FEFA83',
  orange: '#FFD37E',
  red: '#FC6552',
};

/**
 * FULL_PALETTE at `alpha` opacity over white, flattened to opaque hex.
 *
 * Softening by opacity rather than by mixing toward a desaturated tint is
 * deliberate: it keeps the original hue and its bright traffic-light character,
 * where desaturating turns the colors gray and muddy.
 *
 * Flattening rather than emitting rgba() is also deliberate — the result is
 * identical over the white backgrounds these sit on, and solid hex rasterizes
 * predictably in the html2canvas PDF export.
 *
 * Lower alpha = lighter. 1.0 returns FULL_PALETTE exactly.
 */
export function softenPalette(alpha: number): ChartPalette {
  const soften = (hex: string) =>
    '#' + [1, 3, 5]
      .map(i => Math.round(255 - alpha * (255 - parseInt(hex.substr(i, 2), 16))))
      .map(c => c.toString(16).padStart(2, '0').toUpperCase())
      .join('');
  return {
    green: soften(FULL_PALETTE.green),
    yellow: soften(FULL_PALETTE.yellow),
    orange: soften(FULL_PALETTE.orange),
    red: soften(FULL_PALETTE.red),
  };
}

/**
 * The default palette: every gauge arc, reference-range band, BMI band, and
 * section-panel fill. Wide blocks of color on white.
 *
 *   green #83F98A · yellow #FEFA8D · orange #FFD788 · red #FC7160
 */
export const MUTED_PALETTE = softenPalette(0.92);

/**
 * Results Overview per-section marker-distribution bars. Held a notch DARKER
 * than MUTED_PALETTE because these segments are only 7px tall inside a busy
 * card — at the default alpha the green and yellow slivers washed out.
 *
 *   green #7FF886 · yellow #FEFA89 · orange #FFD584 · red #FC6D5B
 */
export const MARKER_BAR_PALETTE = softenPalette(0.95);

/**
 * Comparison-table cell tints. Held a notch LIGHTER than MUTED_PALETTE: that
 * table packs many tinted cells close together, so the full-strength wash is
 * overwhelming across a whole grid.
 *
 *   green #86F98D · yellow #FEFB8F · orange #FFD78B · red #FC7463
 *
 * These values are duplicated by hand as `.bg-green/.bg-yellow/.bg-orange/
 * .bg-red` in src/index.css, since the table tints via CSS class rather than
 * inline style — keep the two in sync. The comparison PDF picks up those
 * classes verbatim, so a change lands on screen and in the export together.
 */
export const TABLE_CELL_PALETTE = softenPalette(0.90);

/*
 * Two palettes deliberately do NOT track these:
 *  - the "Markers Outside Optimal" mini-chart pastels
 *    (#D4EBD5/#F6F0C4/#F9E0C4/#F6CEC6), kept pale on purpose so a long stacked
 *    wall of bars reads calmly;
 *  - the Results Overview STATUS_SOFT pill/tally palette, which needs far more
 *    contrast because text sits directly on top of it.
 */
